import { getWildfireUrl } from "@/lib/product-utils";

export type RedirectResourceType = "product" | "category" | "external" | "brand" | "editorial";
export type RedirectMode = "temporary" | "permanent";

export interface OutboundRedirectRequest {
  destinationUrl: string | null | undefined;
  resourceType: RedirectResourceType;
  resourceId?: string | null;
  merchant?: string | null;
  catalog?: string | null;
  redirectMode?: RedirectMode;
}

export interface KeyedOutboundRedirectRequest extends OutboundRedirectRequest {
  key: string;
}

interface TokenizeResponse {
  token: string;
  redirect_url: string;
  expires_at: number;
}

interface BatchTokenizeResponse {
  items: Array<
    TokenizeResponse & {
      request_id: string;
    }
  >;
  /** Keys whose lookup failed transiently (bridge only); fall back without caching. */
  unavailable?: string[];
}

const DEFAULT_REDIRECT_SERVICE_BASE_URL = "https://go.cosimo.shop";
const DEFAULT_CACHE_TTL_MS = 60 * 60 * 1000;
const redirectCache = new Map<string, { redirectUrl: string; expiresAtMs: number }>();

function isFirstPartyRedirectEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_FIRST_PARTY_REDIRECTS === "true";
}

export function getRedirectServiceBaseUrl(): string {
  const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_SERVICE_BASE_URL;
  return (baseUrl || DEFAULT_REDIRECT_SERVICE_BASE_URL).replace(/\/+$/, "");
}

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

// Same-origin bridge the browser mints through. go.cosimo.shop's mint endpoints
// require the internal service token, so the browser never talks to them: it
// sends a product UUID here, and the server resolves the destination from the
// catalog record and mints with the token. See app/api/outbound/tokenize.
export const OUTBOUND_TOKENIZE_BRIDGE_PATH = "/api/outbound/tokenize";
export const OUTBOUND_TOKENIZE_BATCH_BRIDGE_PATH = "/api/outbound/tokenize/batch";
// Must not exceed MAX_BATCH_MINT_ITEMS in outbound-tokenize-bridge.ts: the
// bridge rejects a larger body outright, which would fall back every key.
export const OUTBOUND_TOKENIZE_BATCH_CHUNK_SIZE = 50;

interface MintCall {
  url: string;
  headers: Record<string, string>;
  body: unknown;
}

/**
 * Build the mint call for one request, or null when it cannot be minted from
 * this runtime: in the browser only product requests with a UUID are mintable
 * (the bridge resolves the destination server-side); on the server the
 * internal token must be configured.
 */
function buildSingleMintCall(request: OutboundRedirectRequest): MintCall | null {
  if (isBrowser()) {
    if (request.resourceType !== "product" || !request.resourceId) return null;
    return {
      url: OUTBOUND_TOKENIZE_BRIDGE_PATH,
      headers: { "Content-Type": "application/json" },
      body: {
        product_uuid: request.resourceId,
        merchant: request.merchant ?? undefined,
        catalog: request.catalog ?? undefined,
        redirect_mode: request.redirectMode ?? "temporary",
      },
    };
  }
  const internalToken = process.env.SHOP_AGENT_INTERNAL_TOKEN?.trim();
  if (!internalToken) return null;
  return {
    url: `${getRedirectServiceBaseUrl()}/api/tokenize`,
    headers: { "Content-Type": "application/json", "X-Octogen-Internal-Token": internalToken },
    body: {
      destination_url: request.destinationUrl,
      resource_type: request.resourceType,
      resource_id: request.resourceId ?? undefined,
      merchant: request.merchant ?? undefined,
      catalog: request.catalog ?? undefined,
      redirect_mode: request.redirectMode ?? "temporary",
    },
  };
}

function buildBatchMintCall(
  items: Array<{ key: string; request: KeyedOutboundRedirectRequest }>
): MintCall | null {
  if (isBrowser()) {
    const mintable = items.filter(
      ({ request }) => request.resourceType === "product" && request.resourceId
    );
    if (mintable.length === 0) return null;
    return {
      url: OUTBOUND_TOKENIZE_BATCH_BRIDGE_PATH,
      headers: { "Content-Type": "application/json" },
      body: {
        items: mintable.map(({ key, request }) => ({
          request_id: key,
          product_uuid: request.resourceId,
          merchant: request.merchant ?? undefined,
          catalog: request.catalog ?? undefined,
          redirect_mode: request.redirectMode ?? "temporary",
        })),
      },
    };
  }
  const internalToken = process.env.SHOP_AGENT_INTERNAL_TOKEN?.trim();
  if (!internalToken) return null;
  return {
    url: `${getRedirectServiceBaseUrl()}/api/tokenize/batch`,
    headers: { "Content-Type": "application/json", "X-Octogen-Internal-Token": internalToken },
    body: {
      items: items.map(({ key, request }) => ({
        request_id: key,
        destination_url: request.destinationUrl,
        resource_type: request.resourceType,
        resource_id: request.resourceId ?? undefined,
        merchant: request.merchant ?? undefined,
        catalog: request.catalog ?? undefined,
        redirect_mode: request.redirectMode ?? "temporary",
      })),
    },
  };
}

function normalizeDestinationUrl(destinationUrl: string | null | undefined): string | null {
  if (!destinationUrl || typeof destinationUrl !== "string") {
    return null;
  }

  const trimmed = destinationUrl.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    const protocol = parsed.protocol.toLowerCase();
    if (protocol !== "http:" && protocol !== "https:") {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

function getLegacyOutboundUrl(destinationUrl: string): string {
  return getWildfireUrl(destinationUrl) || destinationUrl;
}

function buildCacheKey(request: OutboundRedirectRequest): string {
  return JSON.stringify({
    destinationUrl: request.destinationUrl,
    resourceType: request.resourceType,
    resourceId: request.resourceId ?? null,
    merchant: request.merchant ?? null,
    catalog: request.catalog ?? null,
    redirectMode: request.redirectMode ?? "temporary",
  });
}

function readCache(cacheKey: string): string | null {
  const cached = redirectCache.get(cacheKey);
  if (!cached) {
    return null;
  }

  if (Date.now() >= cached.expiresAtMs) {
    redirectCache.delete(cacheKey);
    return null;
  }

  return cached.redirectUrl;
}

function writeCache(cacheKey: string, redirectUrl: string, expiresAt?: number): void {
  const expiresAtMs =
    typeof expiresAt === "number" && Number.isFinite(expiresAt)
      ? expiresAt * 1000
      : Date.now() + DEFAULT_CACHE_TTL_MS;
  redirectCache.set(cacheKey, { redirectUrl, expiresAtMs });
}

export interface OutboundRedirectOptions {
  /**
   * Bypass the in-process token cache and always mint a fresh token.
   * Use when the returned URL is embedded in content cached for longer than
   * the remaining token lifetime (for example ISR-rendered Markdown).
   */
  skipLocalCache?: boolean;
  /**
   * Throw instead of falling back to the untokenized legacy URL when the
   * redirect service call fails in a retryable way (5xx, 429, network error,
   * or a malformed success payload). Use when the caller's output is cached
   * and a degraded (unattributed) link must not be stored for the window.
   * Deterministic 4xx rejections still degrade to the legacy URL: they repeat
   * on every render, so throwing would fail the caller permanently.
   * Has no effect when first-party redirects are disabled: the legacy URL is
   * the intended result then, not a degraded one.
   */
  strict?: boolean;
}

class OutboundRedirectTokenizeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OutboundRedirectTokenizeError";
  }
}

export async function getOutboundRedirectUrl(
  request: OutboundRedirectRequest,
  options: OutboundRedirectOptions = {}
): Promise<string> {
  const normalizedDestination = normalizeDestinationUrl(request.destinationUrl);
  if (!normalizedDestination) {
    return "";
  }

  const legacyUrl = getLegacyOutboundUrl(normalizedDestination);
  if (!isFirstPartyRedirectEnabled()) {
    return legacyUrl;
  }

  const cacheKey = buildCacheKey({
    ...request,
    destinationUrl: normalizedDestination,
  });
  const cached = options.skipLocalCache ? null : readCache(cacheKey);
  if (cached) {
    return cached;
  }

  const mintCall = buildSingleMintCall({ ...request, destinationUrl: normalizedDestination });
  if (!mintCall) {
    return legacyUrl;
  }

  try {
    const response = await fetch(mintCall.url, {
      method: "POST",
      headers: mintCall.headers,
      body: JSON.stringify(mintCall.body),
    });

    if (!response.ok) {
      // Only retryable failures reject: a 4xx is deterministic for this
      // destination, so throwing would fail the caller on every render rather
      // than for one. Degrade to the legacy URL as the HTML PDP does.
      if (options.strict && (response.status >= 500 || response.status === 429)) {
        throw new OutboundRedirectTokenizeError(`tokenize failed: HTTP ${response.status}`);
      }
      return legacyUrl;
    }

    const payload = (await response.json()) as TokenizeResponse;
    if (!payload.redirect_url) {
      if (options.strict) {
        throw new OutboundRedirectTokenizeError("tokenize returned no redirect_url");
      }
      return legacyUrl;
    }

    writeCache(cacheKey, payload.redirect_url, payload.expires_at);
    return payload.redirect_url;
  } catch (error) {
    if (options.strict) {
      throw error;
    }
    return legacyUrl;
  }
}

export async function getBatchOutboundRedirectUrls(
  requests: KeyedOutboundRedirectRequest[]
): Promise<Record<string, string>> {
  const results: Record<string, string> = {};
  if (requests.length === 0) {
    return results;
  }

  const unresolved: Array<{
    key: string;
    cacheKey: string;
    request: KeyedOutboundRedirectRequest;
    fallbackUrl: string;
  }> = [];

  for (const request of requests) {
    const normalizedDestination = normalizeDestinationUrl(request.destinationUrl);
    if (!normalizedDestination) {
      results[request.key] = "";
      continue;
    }

    const fallbackUrl = getLegacyOutboundUrl(normalizedDestination);
    if (!isFirstPartyRedirectEnabled()) {
      results[request.key] = fallbackUrl;
      continue;
    }

    const cacheKey = buildCacheKey({
      ...request,
      destinationUrl: normalizedDestination,
    });
    const cached = readCache(cacheKey);
    if (cached) {
      results[request.key] = cached;
      continue;
    }

    unresolved.push({
      key: request.key,
      cacheKey,
      request: {
        ...request,
        destinationUrl: normalizedDestination,
      },
      fallbackUrl,
    });
  }

  if (unresolved.length === 0) {
    return results;
  }

  // Chunk so a large cart never exceeds the bridge cap; each chunk fails
  // independently, falling back only its own keys.
  const chunks: Array<typeof unresolved> = [];
  for (let i = 0; i < unresolved.length; i += OUTBOUND_TOKENIZE_BATCH_CHUNK_SIZE) {
    chunks.push(unresolved.slice(i, i + OUTBOUND_TOKENIZE_BATCH_CHUNK_SIZE));
  }

  await Promise.all(
    chunks.map(async (chunk) => {
      const mintCall = buildBatchMintCall(chunk);
      if (!mintCall) {
        for (const item of chunk) {
          results[item.key] = item.fallbackUrl;
        }
        return;
      }

      try {
        const response = await fetch(mintCall.url, {
          method: "POST",
          headers: mintCall.headers,
          body: JSON.stringify(mintCall.body),
        });

        if (!response.ok) {
          for (const item of chunk) {
            results[item.key] = item.fallbackUrl;
          }
          return;
        }

        const payload = (await response.json()) as BatchTokenizeResponse;
        const responseByKey = new Map(
          payload.items.map((item) => [item.request_id, item] as const)
        );
        const unavailable = new Set(payload.unavailable ?? []);

        for (const item of chunk) {
          const matched = responseByKey.get(item.key);
          const redirectUrl = matched?.redirect_url || item.fallbackUrl;
          results[item.key] = redirectUrl;
          // A transiently unavailable key must not pin its fallback in the
          // in-process cache; the next call should try to mint again.
          if (!unavailable.has(item.key)) {
            writeCache(item.cacheKey, redirectUrl, matched?.expires_at);
          }
        }
      } catch {
        for (const item of chunk) {
          results[item.key] = item.fallbackUrl;
        }
      }
    })
  );

  return results;
}
